import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Star, MapPin, Users, Bed, Bath, Heart, Share2, Sparkles, Check, Grid3x3, X } from "lucide-react";
import Popover from "@mui/material/Popover";
import Dialog from "@mui/material/Dialog";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { format, differenceInDays } from "date-fns";
import dayjs from "dayjs";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import VillaCard from "@/components/villa/VillaCard";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Eyebrow from "@/components/common/Eyebrow";

function Section({ title, children }) {
  return (
    <Box component="section" sx={{ py: 5, borderBottom: 1, borderColor: "divider" }}>
      <Typography className="font-serif-display" sx={{ fontSize: 24, mb: 3 }}>{title}</Typography>
      {children}
    </Box>
  );
}

export default function VillaDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [villa, setVilla] = useState(null);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [guests, setGuests] = useState(2);
  const [saved, setSaved] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);

  useEffect(() => {
    api.get(`/villas/${id}`).then((r) => {
      setVilla(r.data);
      setSaved(user?.saved_villas?.includes(r.data.id) || false);
    }).catch(() => nav("/villas"));
    window.scrollTo(0, 0);
  }, [id, user, nav]);

  if (!villa) return <Box sx={{ py: 16, textAlign: "center", fontSize: 14, color: "text.secondary" }}>Loading villa…</Box>;

  const nights = range.from && range.to ? Math.max(1, differenceInDays(range.to, range.from)) : 0;
  const basePrice = villa.discount_percent ? Math.round(villa.price_per_night * (1 - villa.discount_percent / 100)) : villa.price_per_night;
  const subtotal = basePrice * nights;
  const serviceFee = Math.round(subtotal * 0.08);
  const taxes = Math.round(subtotal * 0.18);
  const total = subtotal + serviceFee + taxes;

  const proceed = () => {
    if (!nights) { toast("Please select your dates"); return; }
    if (!user) { toast("Please sign in to continue"); nav("/login?redirect=" + encodeURIComponent(`/villas/${id}`)); return; }
    const q = new URLSearchParams({ from: range.from.toISOString(), to: range.to.toISOString(), guests, total });
    nav(`/booking/${id}?${q.toString()}`);
  };

  const toggleSave = async () => {
    if (!user) { toast("Sign in to save"); return; }
    const { data } = await api.post(`/villas/${villa.id}/save`);
    setSaved(data.saved);
    refresh();
  };

  return (
    <Box data-testid="villa-detail">
      {villa.status && villa.status !== "approved" && (
        <Box sx={{ bgcolor: villa.status === "rejected" ? "error.main" : "warning.main", color: "#fff", textAlign: "center", py: 1, fontSize: 13, fontWeight: 500 }} data-testid="villa-status-banner">
          {villa.status === "rejected" ? "This listing was rejected and isn't publicly visible." : "This listing is pending review and isn't publicly visible yet."}
        </Box>
      )}
      <Box sx={{ maxWidth: 1500, mx: "auto", px: { xs: 3, lg: 6 }, pt: 5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 3, flexWrap: "wrap", mb: 4 }}>
          <Box>
            <Eyebrow sx={{ mb: 1.5 }}>{villa.categories?.[0]}</Eyebrow>
            <Typography className="font-serif-display" data-testid="villa-name" sx={{ fontSize: { xs: 32, md: 40, lg: 44 }, letterSpacing: "-0.01em" }}>
              {villa.name}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2, fontSize: 14, color: "text.secondary", flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Star size={13} fill="currentColor" /> {villa.rating} <span>· {villa.reviews_count} reviews</span></Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><MapPin size={13} />{villa.location}</Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button variant="outlined" size="small" startIcon={<Heart size={14} fill={saved ? "currentColor" : "none"} />} onClick={toggleSave} data-testid="detail-save-btn">
              {saved ? "Saved" : "Save"}
            </Button>
            <Button variant="outlined" size="small" startIcon={<Share2 size={14} />} onClick={() => { navigator.clipboard.writeText(window.location.href); toast("Link copied"); }} data-testid="detail-share-btn">
              Share
            </Button>
          </Box>
        </Box>

        {/* GALLERY */}
        <Box sx={{ position: "relative", mb: 8 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gridTemplateRows: "repeat(2, 1fr)", gap: 1, height: { xs: 280, md: 480 }, borderRadius: 4, overflow: "hidden" }} data-testid="gallery">
            <Box component="button" onClick={() => setGalleryOpen(true)} sx={{ gridColumn: { xs: "1 / -1", md: "span 2" }, gridRow: "span 2", border: 0, p: 0, cursor: "pointer", position: "relative" }}>
              <Box component="img" src={villa.images[0]} alt={villa.name} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </Box>
            {villa.images.slice(1, 5).map((img, i) => (
              <Box key={i} component="button" onClick={() => setGalleryOpen(true)} sx={{ display: { xs: "none", md: "block" }, border: 0, p: 0, cursor: "pointer" }}>
                <Box component="img" src={img} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </Box>
            ))}
          </Box>
          <Button
            onClick={() => setGalleryOpen(true)}
            variant="outlined"
            size="small"
            startIcon={<Grid3x3 size={14} />}
            data-testid="show-all-photos-btn"
            sx={{ position: "absolute", bottom: 16, right: 16, bgcolor: "background.paper", "&:hover": { bgcolor: "background.paper" } }}
          >
            Show all photos
          </Button>
        </Box>

        <Dialog open={galleryOpen} onClose={() => setGalleryOpen(false)} fullScreen>
          <Box sx={{ position: "sticky", top: 0, bgcolor: "background.default", zIndex: 1, display: "flex", justifyContent: "flex-end", p: 2 }}>
            <IconButton onClick={() => setGalleryOpen(false)} data-testid="close-gallery-btn"><X size={20} /></IconButton>
          </Box>
          <Box sx={{ maxWidth: 900, mx: "auto", px: 3, pb: 6, display: "flex", flexDirection: "column", gap: 2 }}>
            {villa.images.map((img, i) => (
              <Box key={i} component="img" src={img} alt="" sx={{ width: "100%", borderRadius: 3 }} />
            ))}
          </Box>
        </Dialog>

        <Box sx={{ display: "grid", gridTemplateColumns: { lg: "1fr 400px" }, gap: { xs: 6, lg: 10 } }}>
          {/* MAIN */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 4, pb: 4, borderBottom: 1, borderColor: "divider" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Users size={16} /> <Typography variant="body2">Up to {villa.max_guests} guests</Typography></Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Bed size={16} /> <Typography variant="body2">{villa.bedrooms} bedrooms</Typography></Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Bath size={16} /> <Typography variant="body2">{villa.bathrooms} bathrooms</Typography></Box>
            </Box>

            <Section title="About this villa">
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>{villa.description}</Typography>
              <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box component="img" src={villa.host.avatar} alt="" sx={{ width: 40, height: 40, borderRadius: "50%" }} />
                <Box>
                  <Typography variant="body2" fontWeight={500}>Hosted by {villa.host.name}</Typography>
                  <Typography variant="caption" color="text.secondary">Host since {villa.host.since} · {villa.host.rating}★</Typography>
                </Box>
              </Box>
            </Section>

            <Section title="Amenities">
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr" }, rowGap: 1.5, columnGap: 3 }}>
                {villa.amenities.map((a) => (
                  <Box key={a} sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 14 }}><Check size={14} /> {a}</Box>
                ))}
              </Box>
            </Section>

            <Section title="Experiences nearby">
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {villa.activities.map((a) => <Chip key={a} label={a} variant="outlined" size="small" />)}
              </Box>
              <Box sx={{ mt: 4, display: "grid", gridTemplateColumns: { sm: "repeat(3, 1fr)" }, gap: 2 }}>
                {villa.nearby.map((n) => (
                  <Box key={n.name} sx={{ p: 2, borderRadius: 3, boxShadow: 1 }}>
                    <Typography variant="body2" fontWeight={500}>{n.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{n.distance} away</Typography>
                  </Box>
                ))}
              </Box>
            </Section>

            <Section title="House rules">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, fontSize: 14, color: "text.secondary" }}>
                {villa.house_rules.map((r) => <Box key={r}>· {r}</Box>)}
              </Box>
            </Section>

            <Section title="Cancellation policy">
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{villa.cancellation_policy}</Typography>
            </Section>

            {villa.reviews?.length > 0 && (
              <Section title={`★ ${villa.rating} · ${villa.reviews_count} reviews`}>
                <Box sx={{ display: "grid", gridTemplateColumns: { sm: "1fr 1fr" }, gap: 3 }}>
                  {villa.reviews.slice(0, 4).map((r) => (
                    <Box key={r.id} sx={{ p: 2.5, borderRadius: 3, boxShadow: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                        {[...Array(r.rating)].map((_, i) => <Star key={i} size={12} fill="currentColor" />)}
                      </Box>
                      <Typography variant="body2" sx={{ mb: 1.5 }}>{r.comment}</Typography>
                      <Typography variant="caption" color="text.secondary">— {r.user_name}</Typography>
                    </Box>
                  ))}
                </Box>
              </Section>
            )}
          </Box>

          {/* STICKY BOOKING WIDGET */}
          <Box component="aside">
            <Box sx={{ position: { lg: "sticky" }, top: { lg: 112 }, borderRadius: 3.5, p: 3, boxShadow: 3, bgcolor: "background.paper" }} data-testid="booking-widget">
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 2.5 }}>
                {villa.discount_percent > 0 && <Typography variant="body2" color="text.secondary" sx={{ textDecoration: "line-through" }}>₹{villa.price_per_night.toLocaleString()}</Typography>}
                <Typography className="font-serif-display" sx={{ fontSize: 28 }}>₹{basePrice.toLocaleString()}</Typography>
                <Typography variant="body2" color="text.secondary">night</Typography>
              </Box>

              <Box sx={{ border: 1, borderColor: "divider", borderRadius: 2.5, mb: 1.5, overflow: "hidden" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                  <Box
                    component="button"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    data-testid="widget-dates"
                    sx={{ textAlign: "left", border: 0, borderRight: 1, borderColor: "divider", px: 2, py: 1.25, cursor: "pointer", bgcolor: "transparent", font: "inherit", color: "inherit" }}
                  >
                    <Eyebrow sx={{ fontSize: 9 }}>Check-in</Eyebrow>
                    <Box sx={{ fontSize: 13, mt: 0.25 }}>{range.from ? format(range.from, "dd MMM") : "Add date"}</Box>
                  </Box>
                  <Box
                    component="button"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{ textAlign: "left", border: 0, px: 2, py: 1.25, cursor: "pointer", bgcolor: "transparent", font: "inherit", color: "inherit" }}
                  >
                    <Eyebrow sx={{ fontSize: 9 }}>Check-out</Eyebrow>
                    <Box sx={{ fontSize: 13, mt: 0.25 }}>{range.to ? format(range.to, "dd MMM") : "Add date"}</Box>
                  </Box>
                </Box>
              </Box>
              <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
                <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                  <DatePicker
                    label="Check-in"
                    value={range.from ? dayjs(range.from) : null}
                    onChange={(d) => {
                      const from = d ? d.toDate() : undefined;
                      setRange((r) => ({ ...r, from, to: r.to && from && r.to < from ? undefined : r.to }));
                    }}
                    minDate={dayjs()}
                  />
                  <DatePicker
                    label="Check-out"
                    value={range.to ? dayjs(range.to) : null}
                    onChange={(d) => setRange((r) => ({ ...r, to: d ? d.toDate() : undefined }))}
                    minDate={range.from ? dayjs(range.from) : dayjs()}
                  />
                </Box>
              </Popover>

              <Box sx={{ border: 1, borderColor: "divider", borderRadius: 2.5, px: 2, py: 1.25, mb: 2.5 }}>
                <Eyebrow sx={{ fontSize: 9 }}>Guests</Eyebrow>
                <Box
                  component="input"
                  type="number" min={1} max={villa.max_guests}
                  value={guests}
                  onChange={(e) => setGuests(parseInt(e.target.value || "1", 10))}
                  data-testid="widget-guests"
                  sx={{ width: "100%", bgcolor: "transparent", border: 0, outline: "none", fontSize: 13, fontFamily: "inherit", color: "text.primary", mt: 0.25 }}
                />
              </Box>

              {nights > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2.5, fontSize: 14, pb: 2.5, borderBottom: 1, borderColor: "divider" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}><span>₹{basePrice.toLocaleString()} × {nights} nights</span><span>₹{subtotal.toLocaleString()}</span></Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}><span>Service fee</span><span>₹{serviceFee.toLocaleString()}</span></Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", color: "text.secondary" }}><span>Taxes</span><span>₹{taxes.toLocaleString()}</span></Box>
                </Box>
              )}
              {nights > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 500, mb: 2.5 }}>
                  <span>Total</span><span>₹{total.toLocaleString()}</span>
                </Box>
              )}

              <Button onClick={proceed} variant="contained" fullWidth data-testid="reserve-btn">
                {nights > 0 ? "Reserve" : "Check availability"}
              </Button>
              <Box sx={{ mt: 2, fontSize: 12, color: "text.secondary", textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 0.75 }}>
                <Sparkles size={11} /> No charge until you confirm
              </Box>
            </Box>
          </Box>
        </Box>

        {/* SIMILAR */}
        {villa.similar?.length > 0 && (
          <Box component="section" sx={{ py: 12 }}>
            <Box sx={{ mb: 5 }}>
              <Eyebrow sx={{ mb: 1.5 }}>You may also love</Eyebrow>
              <Typography className="font-serif-display" sx={{ fontSize: { xs: 30, md: 36 } }}>Similar residences</Typography>
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 3 }}>
              {villa.similar.map((v) => <VillaCard key={v.id} villa={v} />)}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
